import styled from '@emotion/styled';
import { colors, fonts, radii, breakpoints } from './tokens';
import { ChevronLeftIcon, ChevronRightIcon } from './icons';

// ---------------------------------------------------------------------------------------------
// EyeResultsTable - Figma results table: teal "Right Eye | Left Eye" header cells, a plain value
// row, a grey "N/A" row and a dark notation-switcher row ("< European Snellen >").
// ---------------------------------------------------------------------------------------------

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(${({ cols }) => cols}, minmax(0, 1fr));
  column-gap: ${({ gap }) => gap}px;
  row-gap: 0;
  width: 100%;
  max-width: ${({ cols }) => cols * 280}px;
  margin: 0 auto;
  font-family: ${fonts.family};
  @media (max-width: ${breakpoints.phone - 1}px) {
    column-gap: 8px;
  }
`;

const Cell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 56px;
  padding: 8px 12px;
  box-sizing: border-box;
  text-align: center;
  font-size: 14px;
  line-height: 1.35;
  color: ${colors.text};
  ${({ tone }) =>
    ({
      header: `background:${colors.tableHeader};color:#fff;font-weight:700;min-height:56px;`,
      muted: `background:${colors.notApplicable};color:${colors.slate[700]};`,
      notation: `background:${colors.notationBar};color:#fff;padding:0 4px;`,
      plain: '',
      emphasis: `font-weight:700;color:${colors.slate[800]};`,
    })[tone || 'plain']}
  @media (max-width: ${breakpoints.phone - 1}px) {
    font-size: 12px;
    min-height: 44px;
  }
`;

/**
 * @param columns  header labels, e.g. ['Right Eye', 'Left Eye'] (or ['logMAR','Right Eye','Left Eye','Binocular'])
 * @param rows     [{ tone?: 'plain'|'muted'|'notation'|'emphasis', cells: [node, node, ...] }]
 * @param gap      column gap px (Figma: 24)
 */
export const EyeResultsTable = ({ columns = ['Right Eye', 'Left Eye'], rows = [], gap = 24, ...rest }) => (
  <Grid cols={columns.length} gap={gap} role='table' {...rest}>
    {columns.map((c) => (
      <Cell key={`h-${c}`} tone='header' role='columnheader'>
        {c}
      </Cell>
    ))}
    {rows.map((row, r) =>
      row.cells.map((cell, c) => (
        <Cell key={`${r}-${c}`} tone={row.tone} role='cell'>
          {cell}
        </Cell>
      )),
    )}
  </Grid>
);

const SwitchRoot = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 4px;
  button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 44px;
    border: 0;
    background: transparent;
    color: #fff;
    cursor: pointer;
    border-radius: 4px;
    &:hover {
      background: rgba(255, 255, 255, 0.12);
    }
    &:focus-visible {
      outline: 2px solid ${colors.brand[300]};
    }
  }
  span {
    flex: 1;
    min-width: 0;
    text-align: center;
    font-size: 14px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  @media (max-width: ${breakpoints.phone - 1}px) {
    button {
      width: 24px;
    }
    span {
      font-size: 11px;
    }
  }
`;

// Default acuity notations offered by the "< European Snellen >" switcher.
export const ACUITY_NOTATIONS = ['European Snellen', 'US Snellen', 'Decimal', 'logMAR'];

/** NotationSwitcher - "< European Snellen >" cycling control used in the dark results row. */
export const NotationSwitcher = ({ options = ACUITY_NOTATIONS, value, onChange, label = 'notation' }) => {
  const i = Math.max(0, options.indexOf(value));
  const go = (d) => onChange?.(options[(i + d + options.length) % options.length]);
  return (
    <SwitchRoot>
      <button type='button' aria-label={`Previous ${label}`} onClick={() => go(-1)}>
        <ChevronLeftIcon size={18} strokeWidth={2.5} />
      </button>
      <span aria-live='polite'>{options[i]}</span>
      <button type='button' aria-label={`Next ${label}`} onClick={() => go(1)}>
        <ChevronRightIcon size={18} strokeWidth={2.5} />
      </button>
    </SwitchRoot>
  );
};

// ---------------------------------------------------------------------------------------------
// DataTable - Completed Examinations style: teal header, zebra rows, scrolls inside its card.
// ---------------------------------------------------------------------------------------------

const Scroll = styled.div`
  width: 100%;
  max-height: ${({ maxHeight }) => maxHeight};
  overflow: auto;
  border-radius: ${radii.card}px;
  box-shadow: 0 0 0 1px ${colors.slate[200]};
  -webkit-overflow-scrolling: touch;
`;

const T = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-family: ${fonts.family};
  font-size: 14px;
  color: ${colors.text};
  thead th {
    position: sticky;
    top: 0;
    z-index: 1;
    background: ${colors.tableHeader};
    color: #fff;
    font-weight: 600;
    padding: 12px 10px;
    text-align: center;
    white-space: nowrap;
  }
  thead th + th {
    border-left: 1px solid rgba(255, 255, 255, 0.35);
  }
  tbody tr:nth-of-type(even) {
    background: ${colors.tableRowAlt};
  }
  tbody td {
    padding: 12px 10px;
    text-align: center;
    height: 52px;
    box-sizing: border-box;
  }
  @media (max-width: ${breakpoints.phone - 1}px) {
    font-size: 12px;
    thead th,
    tbody td {
      padding: 10px 6px;
    }
    thead th {
      white-space: normal;
      line-height: 1.2;
    }
  }
`;

const Empty = styled.div`
  padding: 32px 16px;
  text-align: center;
  color: ${colors.textMuted};
  font-family: ${fonts.family};
  font-size: 14px;
`;

/**
 * @param columns  [{ key, label, align?, width?, render?(row) }]
 * @param rows     array of objects (row.id used as key)
 * @param empty    node shown when rows is empty
 * @param maxHeight CSS max-height of the scroll area (default 100%)
 */
export const DataTable = ({ columns, rows, empty = 'Nothing to show yet.', maxHeight = '100%', caption, ...rest }) => (
  <Scroll maxHeight={maxHeight} {...rest}>
    <T>
      {caption && <caption style={{ position: 'absolute', left: -9999 }}>{caption}</caption>}
      <thead>
        <tr>
          {columns.map((c) => (
            <th key={c.key} style={{ width: c.width, textAlign: c.align }} scope='col'>
              {c.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={row.id ?? i}>
            {columns.map((c) => (
              <td key={c.key} style={{ textAlign: c.align }}>
                {c.render ? c.render(row) : row[c.key]}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </T>
    {rows.length === 0 && <Empty>{empty}</Empty>}
  </Scroll>
);

// Borderless icon action for table cells (View Results arrow, Delete trash).
export const TableAction = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 36px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: ${({ tone }) => (tone === 'danger' ? colors.trash : colors.tableHeader)};
  cursor: pointer;
  &:hover:not(:disabled) {
    background: ${({ tone }) => (tone === 'danger' ? colors.error[50] : colors.brand[50])};
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
  &:focus-visible {
    outline: 2px solid ${colors.brand[300]};
  }
`;

export default DataTable;
